'use client'

import React, { useState } from 'react'
import { Plus } from '@phosphor-icons/react'
import FractionDisplay from '@/components/FractionDisplay'
import Link from 'next/link'
import DOMPurify from 'isomorphic-dompurify'

interface RecipeMobileTabsProps {
  recipe: any
  recipeNotes: any[]
  userId?: number
  onAddNote: () => void
}

type TabKey = 'ingredients' | 'directions' | 'notes' | 'tags'

const tabs: { key: TabKey; label: string }[] = [
  { key: 'ingredients', label: 'Ingredients' },
  { key: 'directions', label: 'Directions' },
  { key: 'notes', label: 'Notes' },
  { key: 'tags', label: 'Tags' },
]

export function RecipeMobileTabs({ recipe, recipeNotes, userId, onAddNote }: RecipeMobileTabsProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('ingredients')

  return (
    <div className="w-full">
      {/* Tab bar */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar bg-gray-100 rounded-full">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-shrink-0 px-4 py-2 text-sm font-medium rounded-full transition-colors ${
              activeTab === tab.key
                ? 'bg-[#85C349] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
            {tab.key === 'ingredients' && recipe?.recipe_ingredients?.length > 0 && (
              <span className={`ml-1 text-xs ${activeTab === tab.key ? 'text-white/80' : 'text-gray-400'}`}>({recipe.recipe_ingredients.length})</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="py-4">
        {activeTab === 'ingredients' && (
          <div>
            {recipe?.recipe_ingredients?.length > 0 ? (
              <div className="flex flex-col gap-4">
                {[...recipe.recipe_ingredients]
                  .sort((a: any, b: any) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))
                  .map((item: any, index: number) => (
                    <div
                      key={item?.record_id ?? `${item?.ingredient_id}-${index}`}
                      className="flex items-start gap-3 text-sm leading-[17px] text-[#0D1000]"
                    >
                      <span className="min-w-[28px] shrink-0 font-semibold">
                        <FractionDisplay
                          whole_nmuber={Number(item?.whole_number)}
                          numerator={Number(item?.numerator)}
                          denominator={Number(item?.denominator)}
                        />
                      </span>
                      <span className="w-[60px] shrink-0">
                        {item?.unit_of_measurement?.unit_abbreviation ?? ''}
                      </span>
                      <span
                        className="min-w-0 flex-1"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item?.ingredient?.ingredient_name ?? ''),
                        }}
                      />
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-6">No ingredients listed</p>
            )}
          </div>
        )}

        {activeTab === 'directions' && (
          <div className="space-y-4">
            {recipe?.recipe_steps?.length > 0 ? (
              [...recipe.recipe_steps]
                .sort((a: any, b: any) => (a?.step_order ?? 0) - (b?.step_order ?? 0))
                .map((step: any, index: number) => (
                  <div key={index} className="flex gap-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#EAF3DE] flex items-center justify-center">
                      <span className="text-xs font-semibold text-[#548828]">{index + 1}</span>
                    </div>
                    <div
                      className="text-sm text-gray-700 leading-relaxed pt-1"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(step?.step_name as string ?? '') }}
                    />
                  </div>
                ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-6">No directions available</p>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div>
            {userId === recipe?.user_id && (
              <div className="flex justify-end mb-3">
                <button
                  onClick={onAddNote}
                  className="flex items-center gap-1.5 rounded-full border border-[#85C349] px-3 py-1.5 text-xs text-[#85C349] hover:bg-green-50 transition-colors"
                >
                  <Plus size={12} />
                  Add note
                </button>
              </div>
            )}
            {recipeNotes?.length > 0 ? (
              <ol className="list-decimal list-inside space-y-2">
                {[...recipeNotes]
                  .sort((a: any, b: any) => (a?.note_order ?? 0) - (b?.note_order ?? 0))
                  .map((note: any, index: number) => (
                    <li key={index} className="text-sm text-gray-700 leading-relaxed">
                      <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note?.note_name as string ?? '') }} />
                    </li>
                  ))}
              </ol>
            ) : (
              <p className="text-sm text-gray-400 text-center py-6">No notes yet</p>
            )}
          </div>
        )}

        {activeTab === 'tags' && (
          <div>
            {recipe?.categories?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {recipe.categories.map((tag: any, index: number) => (
                  <span
                    key={index}
                    className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700"
                  >
                    {tag?.category_name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-6">No tags</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
